import reactUpdate from 'immutability-helper';
import * as PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import {BenchlingState} from 'redux-stores/reduceApp';
import {ButtonStyle} from 'ui-library/buttons/Button';
import LegacyDropdownMenuItem from 'ui-library/dropdowns/LegacyDropdownMenuConstants';
import LegacySelectDropdown from 'ui-library/dropdowns/LegacySelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {TextInputType} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import * as modals from 'util/Modals';
import {InputChangeEvent} from 'util/Types';
import ExperimentalConditionTable from 'workflows/components/ExperimentalConditionTable';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import {
  ExperimentalCondition,
  ExperimentalConditionValue,
  STAGE_CONFIGURATION_FULL_SHAPE,
  StageConfiguration,
  StageConfigurationSummary,
  WORKFLOW_TEMPLATE_VERSION_FULL_SHAPE,
  WorkflowTemplateVersion,
} from 'workflows/WorkflowConstants';
import * as WorkflowTemplateActions from 'workflows/WorkflowTemplateActions';

function mapStateToProps(
  state: BenchlingState,
  {
    baseWorkflowTemplateVersionId,
    workflowTemplateVersionId,
  }: {
    baseWorkflowTemplateVersionId?: string | null;
    workflowTemplateVersionId: string;
  }
): {
  baseWorkflowTemplateVersion: WorkflowTemplateVersion | null | undefined;
  workflowTemplateVersion: WorkflowTemplateVersion;
} {
  const workflowSelector = new WorkflowSelector(state.models.workflows);
  return {
    baseWorkflowTemplateVersion: baseWorkflowTemplateVersionId
      ? workflowSelector.getWorkflowTemplateVersion(baseWorkflowTemplateVersionId)
      : null,
    workflowTemplateVersion: workflowSelector.getWorkflowTemplateVersion(workflowTemplateVersionId)!,
  };
}

interface ConfigureStageModalProps {
  // If null or undefined, this workflow was created from scratch
  baseWorkflowTemplateVersionId?: string | null;
  // If stageConfiguration is specified the modal is editing the existing stage
  stageConfiguration: StageConfiguration | null;
  workflowTemplateVersionId: string;
  onRequestClose: () => void;
  // The index is only used when creating a new stage
  index?: number | null;

  // Props provided by connect
  baseWorkflowTemplateVersion: WorkflowTemplateVersion | null | undefined;
  workflowTemplateVersion: WorkflowTemplateVersion;
}

interface State {
  stageName: string;
  experimentalConditions: Array<ExperimentalCondition>;
  // StageConfiguration from which to clone the entry template, if different from currently edited stage
  baseStageConfigurationSummary: StageConfigurationSummary | null;
}

class ConfigureStageModal extends React.Component<ConfigureStageModalProps, State> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    // If null or undefined, this workflow was created from scratch
    baseWorkflowTemplateVersionId: PropTypes.string,
    // If stageConfiguration is specified the modal is editing the existing stage
    stageConfiguration: STAGE_CONFIGURATION_FULL_SHAPE,
    workflowTemplateVersionId: PropTypes.string.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    // The index is only used when creating a new stage
    index: PropTypes.number,

    // Props provided by connect
    baseWorkflowTemplateVersion: WORKFLOW_TEMPLATE_VERSION_FULL_SHAPE,
    workflowTemplateVersion: WORKFLOW_TEMPLATE_VERSION_FULL_SHAPE.isRequired,
  };

  state: State = {
    stageName: this.props.stageConfiguration ? this.props.stageConfiguration.stageName : '',
    experimentalConditions: this.getInitialExperimentalConditions(),
    baseStageConfigurationSummary: this.props.stageConfiguration,
  };

  getInitialExperimentalConditions(): Array<ExperimentalCondition> {
    let experimentalConditions = this.props.stageConfiguration
      ? this.props.stageConfiguration.experimentalConditions
      : [];
    if (experimentalConditions.length === 0) {
      experimentalConditions = [{id: null, name: '', values: []}];
    }
    return experimentalConditions;
  }

  async componentDidMount(): Promise<void> {
    if (this.props.baseWorkflowTemplateVersionId && !this.props.baseWorkflowTemplateVersion) {
      try {
        // In the future, the performance can be improved by fetching only the workflow template version dict
        await WorkflowTemplateActions.fetchWorkflowTemplateVersionDetails(
          this.props.baseWorkflowTemplateVersionId
        );
      } catch (err) {
        // If we don't have permission to the base workflow template version, silently fail without
        // displaying its entry templates.
        if (err.status !== 404) {
          throw err;
        }
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleStageNameChange = (e: InputChangeEvent) => {
    this.setState({stageName: e.target.value});
  };

  _isStageNamed(): boolean {
    return this.state.stageName.trim() !== '';
  }

  _areConditionsNamed(): boolean {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const isValueNamed = (value: ExperimentalConditionValue) => value.textValue.trim() !== '';
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const hasOptions = (condition: ExperimentalCondition) =>
      condition.values.some((value) => isValueNamed(value));
    const nonEmptyConditions = this.state.experimentalConditions.filter((cond) => hasOptions(cond));
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const isNamed = (condition: ExperimentalCondition) => condition.name.trim() !== '';
    return nonEmptyConditions.every((condition) => isNamed(condition));
  }

  _isSubmitEnabled(): boolean {
    return this._isStageNamed() && this._areConditionsNamed();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleDelete = () => {
    const stageConfiguration = this.props.stageConfiguration;
    if (stageConfiguration) {
      modals
        .confirm(
          'Confirm Delete Stage',
          'Are you sure you want to delete this stage? This action cannot be undone.',
          'danger'
        )
        .done(() => {
          WorkflowTemplateActions.deleteStageConfiguration(stageConfiguration.id);
          this.props.onRequestClose();
        });
    }
  };

  _getButtons(): Array<ModalButton> {
    return _.compact([
      this.props.stageConfiguration && {
        text: `Delete stage`,
        style: ButtonStyle.NEGATIVE,
        pullLeft: true,
        onClick: this._handleDelete,
      },
      {
        text: this.props.stageConfiguration ? 'Save' : 'Create stage',
        style: ButtonStyle.PRIMARY,
        disabled: !this._isSubmitEnabled(),
        onClick: this._handleSubmit,
      },
    ]);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleSubmit = () => {
    const nonEmptyExperimentalConditions = this.state.experimentalConditions
      .filter((condition) => condition.name.trim() !== '')
      .map((condition) => ({
        ...condition,
        values: condition.values.filter((value) => value.textValue.trim() !== ''),
      }));

    const commonFields = {
      stageName: this.state.stageName,
      experimentalConditions: nonEmptyExperimentalConditions,
      stageConfigurationIdForEntryTemplate: this.state.baseStageConfigurationSummary
        ? this.state.baseStageConfigurationSummary.id
        : null,
    };
    if (this.props.stageConfiguration) {
      WorkflowTemplateActions.editStageConfiguration(this.props.stageConfiguration.id, commonFields);
    } else {
      WorkflowTemplateActions.createStageConfiguration({
        workflowTemplateVersionId: this.props.workflowTemplateVersionId,
        entryTemplateName: this.state.stageName,
        index: this.props.index,
        ...commonFields,
      });
    }
    this.props.onRequestClose();
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleCreateExperimentalCondition = () => {
    this.setState(
      reactUpdate(this.state, {
        experimentalConditions: {
          $push: [
            {
              id: null,
              name: '',
              values: [
                {
                  id: undefined,
                  name: '',
                  textValue: '',
                },
              ],
            },
          ],
        },
      })
    );
  };

  _handleCreateExperimentalConditionValue({conditionIndex}: {conditionIndex: number}): void {
    this.setState(
      reactUpdate(this.state, {
        experimentalConditions: {
          [conditionIndex]: {
            values: {
              $push: [
                {
                  id: undefined,
                  name: '',
                  textValue: '',
                },
              ],
            },
          },
        },
      })
    );
  }

  _handleEditExperimentalConditionName({conditionIndex, name}: {conditionIndex: number; name: string}): void {
    this.setState(
      reactUpdate(this.state, {
        experimentalConditions: {
          [conditionIndex]: {
            $merge: {name},
          },
        },
      })
    );
  }

  _handleEditExperimentalConditionValue({
    conditionIndex,
    valueIndex,
    textValue,
  }: {
    conditionIndex: number;
    valueIndex: number;
    textValue: string;
  }): void {
    this.setState(
      reactUpdate(this.state, {
        experimentalConditions: {
          [conditionIndex]: {
            values: {
              [valueIndex]: {$merge: {textValue}},
            },
          },
        },
      })
    );
  }

  _handleDeleteExperimentalCondition({conditionIndex}: {conditionIndex: number}): void {
    this.setState(
      reactUpdate(this.state, {
        experimentalConditions: {
          $splice: [[conditionIndex, 1]],
        },
      })
    );
  }

  _handleDeleteExperimentalConditionValue({
    conditionIndex,
    valueIndex,
  }: {
    conditionIndex: number;
    valueIndex: number;
  }): void {
    this.setState(
      reactUpdate(this.state, {
        experimentalConditions: {
          [conditionIndex]: {
            values: {
              $splice: [[valueIndex, 1]],
            },
          },
        },
      })
    );
  }

  _getEntryTemplateDropdownDisplayName(): string {
    if (!this.state.baseStageConfigurationSummary) {
      return 'None';
    } else {
      return this.state.baseStageConfigurationSummary.entryTemplateName;
    }
  }

  /**
   * Requires baseWorkflowTemplateVersion to be loaded.
   */
  _getEntryTemplateDropdownItems(): Array<Array<LegacyDropdownMenuItem>> {
    return [[this._getNewEntryTemplateItem(), ...this._getBaseEntryTemplateItems()]];
  }

  _getNewEntryTemplateItem(): LegacyDropdownMenuItem {
    return {
      name: 'None',
      handleOnClick: () => {
        this.setState({
          baseStageConfigurationSummary: null,
        });
      },
    };
  }

  _getBaseEntryTemplateItems(): Array<LegacyDropdownMenuItem> {
    const sourceWorkflowTemplateVersion = this.props.baseWorkflowTemplateVersion
      ? this.props.baseWorkflowTemplateVersion
      : this.props.workflowTemplateVersion;
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const makeItem = (stageConfigurationSummary: StageConfigurationSummary) => ({
      name: stageConfigurationSummary.entryTemplateName,
      handleOnClick: () => {
        this._setBaseStageConfigurationSummary(stageConfigurationSummary);
      },
    });
    return sourceWorkflowTemplateVersion.stageConfigurations.map((config) => makeItem(config));
  }

  _setBaseStageConfigurationSummary(stageConfigurationSummary: StageConfigurationSummary): void {
    this.setState({
      baseStageConfigurationSummary: stageConfigurationSummary,
    });
  }

  render(): JSX.Element {
    return (
      <Modal
        title={this.props.stageConfiguration ? 'Edit stage' : 'Create stage'}
        onRequestClose={this.props.onRequestClose}
        buttons={this._getButtons()}
      >
        <div>
          <div className="u-spacing-bottom">
            <TextField
              autoFocus={true}
              type={TextInputType.TEXT}
              label="Stage name"
              labelStyle={TextField.LABEL_STYLES.LABEL}
              value={this.state.stageName}
              onChange={this._handleStageNameChange}
              placeholder="Enter stage name here"
            />
          </div>
          <div className="u-spacing-bottom">
            <FieldLabel label="Entry template" />
            <LegacySelectDropdown
              displayName={this._getEntryTemplateDropdownDisplayName()}
              items={this._getEntryTemplateDropdownItems()}
            />
          </div>
          <FieldLabel
            label={
              <div className="u-verticallyCenterItems">
                <span>Experimental conditions</span>
                <BenchlingIcon
                  className="workflowConfigureScreen-compose u-pointer"
                  icon="compose"
                  onClick={this._handleCreateExperimentalCondition}
                />
              </div>
            }
          />
          {this.state.experimentalConditions.map((experimentalCondition, conditionIndex) => (
            <div key={conditionIndex}>
              <ExperimentalConditionTable
                experimentalCondition={experimentalCondition}
                onEditName={(name) => {
                  this._handleEditExperimentalConditionName({conditionIndex, name});
                }}
                onDeleteCondition={() => {
                  this._handleDeleteExperimentalCondition({conditionIndex});
                }}
                onCreateValue={() => {
                  this._handleCreateExperimentalConditionValue({conditionIndex});
                }}
                onDeleteValue={(valueIndex) => {
                  this._handleDeleteExperimentalConditionValue({conditionIndex, valueIndex});
                }}
                onEditValue={(valueIndex, textValue) => {
                  this._handleEditExperimentalConditionValue({conditionIndex, valueIndex, textValue});
                }}
              />
            </div>
          ))}
        </div>
      </Modal>
    );
  }
}

export default connect(mapStateToProps)(ConfigureStageModal);
